// 外国人情報のファイル取り込み (Excel/CSV) の列定義。
// テンプレート生成 / ヘッダ検証 / 行パース で共有する単一定義にし、列の追加・並べ替えが1箇所で済むようにする。
// 列順・列名は配布テンプレート「外国人情報_インポート用.xlsx」と完全一致させる (不一致のファイルは取り込み不可)。

import { FILE_INCORRECT } from "@/features/company/constants/excel";

export const LEARNER_IMPORT_SHEET_NAME = "外国人情報";
// 選択肢 (所属機関 / 国籍 / 受験分野 / 日本語能力 / 性別 / ステータス) を置く非表示シート。入力規則のドロップダウン元になる。
export const LEARNER_IMPORT_MASTER_SHEET_NAME = "Master";

// テンプレートで入力規則 (ドロップダウン) を張る行数。取り込み上限 (50件) より余裕を持たせる。
export const LEARNER_IMPORT_TEMPLATE_ROWS = 200;

// テンプレートの書式指定。適用範囲が値によって違う点に注意。
// text: 入力欄 (2行目〜) の全行に Excel の「文字列」書式 (@) を張る。管理番号が数値化 (先頭 0 落ち・指数表記) されるのを防ぐ。
// date: 記入例セルを日付セルにし、表示書式 yyyy/mm/dd を入力欄 (2行目〜) の全行に張る。
// number: 記入例セルを数値セルにする。
export type TLearnerImportColumnFormat = "text" | "date" | "number";

type TLearnerImportColumn = {
  // パース結果のオブジェクトキー
  key: string;
  // Excel のヘッダ文字列 (検証で完全一致を要求する)
  header: string;
  // テンプレートの列幅
  width: number;
  // テンプレートのサンプル行に入れる値 (文字列で持ち、format に応じて日付・数値セルへ変換する)
  sample: string;
  // 書式指定 (未指定は Excel の「標準」)。text / date は入力欄の全行、number は記入例セルにだけ効く。
  format?: TLearnerImportColumnFormat;
};

export const LEARNER_IMPORT_COLUMNS = [
  { key: "managementNumber", header: "管理番号", width: 16, sample: "2313231", format: "text" },
  { key: "fullName", header: "氏名", width: 20, sample: "PHUONG" },
  { key: "companyName", header: "所属機関", width: 30, sample: "おはなファミリーレストラン" },
  { key: "industryName", header: "受験分野", width: 20, sample: "外食業" },
  { key: "nickName", header: "呼び名", width: 16, sample: "フォン" },
  { key: "nationality", header: "国籍", width: 14, sample: "ベトナム" },
  { key: "gender", header: "性別", width: 10, sample: "男性" },
  { key: "status", header: "ステータス", width: 14, sample: "準備中" },
  { key: "clause1StartDate", header: "1号の起算日", width: 16, sample: "2024/01/01", format: "date" },
  { key: "blankPeriodMonths", header: "ブランク期間（月単位）", width: 22, sample: "1", format: "number" },
  { key: "province", header: "居住地（都道府県）", width: 18, sample: "東京都" },
  { key: "municipality", header: "居住地（市区町村）", width: 20, sample: "渋谷区" },
  { key: "japaneseLevel", header: "日本語能力", width: 14, sample: "N4" },
  { key: "lastExamDate", header: "前回試験の受験日", width: 18, sample: "2025/04/01", format: "date" },
  { key: "lastScore", header: "前回点数", width: 12, sample: "650", format: "number" },
] as const satisfies readonly TLearnerImportColumn[];

export type TLearnerImportColumnKey = (typeof LEARNER_IMPORT_COLUMNS)[number]["key"];

// 列定義を扱う側の型。`as const` のままだと format を持たない列で `col.format` が型エラーになるため、
// key だけリテラル (行データのキーとして使う) に保ったまま他のプロパティを広げる。
export type TLearnerImportColumnDef = Omit<TLearnerImportColumn, "key"> & { key: TLearnerImportColumnKey };

// 1行分の生データ (すべて文字列に正規化済み)。
export type TLearnerRowExcel = Record<TLearnerImportColumnKey, string>;

// 企業アカウントは所属機関 (CompanyInRSO) を持たないため、テンプレート・取り込みの両方で所属機関列を扱わない。
const COMPANY_USER_EXCLUDED_KEYS: TLearnerImportColumnKey[] = ["companyName"];

// ログインユーザ種別に応じた列定義。テンプレート生成 / ヘッダ検証 / 行パース はすべてこれを基準にする。
export const getLearnerImportColumns = (isCompanyUser: boolean): TLearnerImportColumnDef[] =>
  isCompanyUser ? LEARNER_IMPORT_COLUMNS.filter((col) => !COMPANY_USER_EXCLUDED_KEYS.includes(col.key)) : [...LEARNER_IMPORT_COLUMNS];

export const getLearnerImportHeaders = (isCompanyUser: boolean): string[] => getLearnerImportColumns(isCompanyUser).map((col) => col.header);

// 空白除去 + NFKC で突合する (Excel の表記ゆれ対策。「（月単位）」の全角括弧が半角で入っていても通す)。
const normalizeHeader = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFKC")
    .replace(/[\s　]/g, "");

// ヘッダ行がテンプレートと一致するか。列数・列名・順序のいずれかが違えば取り込み不可とする。
// 期待するヘッダはログインユーザ種別で変わる (企業アカウントは所属機関列なし)。
export const validateLearnerFileHeader = (rowHeader: unknown[] | undefined, isCompanyUser: boolean) => {
  const expected = getLearnerImportHeaders(isCompanyUser);
  // 末尾の空セルだけ落とす (Excel が余分な空列を返すことがあるため)。
  // 途中の空列は落とさない: 行データは列位置 (row[index]) で読むため、ここで詰めるとヘッダだけ一致して
  // データが1列ずれたまま取り込まれてしまう (例: 前回試験の受験日が前回点数として読まれる)。
  const header = (rowHeader ?? []).map(normalizeHeader);
  while (header.length > 0 && header[header.length - 1] === "") header.pop();

  if (header.length !== expected.length) throw new Error(FILE_INCORRECT);

  for (let i = 0; i < expected.length; i++) {
    if (header[i] !== normalizeHeader(expected[i])) throw new Error(FILE_INCORRECT);
  }

  return true;
};

// 全セルが空の行か (Excel の末尾に残る空行を取り込み対象から外す)。
export const isEmptyExcelRow = (row: unknown[] | undefined): boolean =>
  !row || row.length === 0 || row.every((cell) => String(cell ?? "").trim() === "");
